// Role-based access control types and definitions

export type UserRole = 'Administrator' | 'PowerUser' | 'ReadOnly';

export interface Permission {
  id: string;
  name: string;
  description: string;
  requiredRole: UserRole;
}

// Define all permissions in the application
export const PERMISSIONS = {
  // User Management
  USER_MANAGEMENT_VIEW: {
    id: 'user_management_view',
    name: 'View User Management',
    description: 'View user list and user details',
    requiredRole: 'ReadOnly' as UserRole,
  },
  USER_MANAGEMENT_CREATE: {
    id: 'user_management_create',
    name: 'Create Users',
    description: 'Invite new users to the system',
    requiredRole: 'Administrator' as UserRole,
  },
  USER_MANAGEMENT_UPDATE: {
    id: 'user_management_update',
    name: 'Update Users',
    description: 'Modify user information and roles',
    requiredRole: 'Administrator' as UserRole,
  },
  USER_MANAGEMENT_DELETE: {
    id: 'user_management_delete',
    name: 'Delete Users',
    description: 'Remove users from the system',
    requiredRole: 'Administrator' as UserRole,
  },
  USER_MANAGEMENT_DISABLE: {
    id: 'user_management_disable',
    name: 'Disable Users',
    description: 'Enable/disable user accounts',
    requiredRole: 'Administrator' as UserRole,
  },

  // Rule Management
  RULE_MANAGEMENT_VIEW: {
    id: 'rule_management_view',
    name: 'View Rules',
    description: 'View tagging rules and configurations',
    requiredRole: 'ReadOnly' as UserRole,
  },
  RULE_MANAGEMENT_CREATE: {
    id: 'rule_management_create',
    name: 'Create Rules',
    description: 'Create new tagging rules',
    requiredRole: 'PowerUser' as UserRole,
  },
  RULE_MANAGEMENT_UPDATE: {
    id: 'rule_management_update',
    name: 'Update Rules',
    description: 'Modify existing tagging rules',
    requiredRole: 'PowerUser' as UserRole,
  },
  RULE_MANAGEMENT_DELETE: {
    id: 'rule_management_delete',
    name: 'Delete Rules',
    description: 'Delete tagging rules',
    requiredRole: 'PowerUser' as UserRole,
  },

  // Service Configuration
  SERVICE_CONFIG_VIEW: {
    id: 'service_config_view',
    name: 'View Service Configuration',
    description: 'View service configurations and settings',
    requiredRole: 'ReadOnly' as UserRole,
  },
  SERVICE_CONFIG_UPDATE: {
    id: 'service_config_update',
    name: 'Update Service Configuration',
    description: 'Modify service configurations',
    requiredRole: 'PowerUser' as UserRole,
  },

  // Resource Scanning
  RESOURCE_SCAN_VIEW: {
    id: 'resource_scan_view',
    name: 'View Resource Scans',
    description: 'View resource scanning results and history',
    requiredRole: 'ReadOnly' as UserRole,
  },
  RESOURCE_SCAN_EXECUTE: {
    id: 'resource_scan_execute',
    name: 'Execute Resource Scans',
    description: 'Run resource scans and apply tags',
    requiredRole: 'PowerUser' as UserRole,
  },

  // AWS Account Management
  AWS_ACCOUNT_VIEW: {
    id: 'aws_account_view',
    name: 'View AWS Accounts',
    description: 'View AWS account configurations',
    requiredRole: 'ReadOnly' as UserRole,
  },
  AWS_ACCOUNT_MANAGE: {
    id: 'aws_account_manage',
    name: 'Manage AWS Accounts',
    description: 'Add, update, or remove AWS accounts',
    requiredRole: 'PowerUser' as UserRole,
  },

  // System Settings
  SYSTEM_SETTINGS_VIEW: {
    id: 'system_settings_view',
    name: 'View System Settings',
    description: 'View system configuration and settings',
    requiredRole: 'ReadOnly' as UserRole,
  },
  SYSTEM_SETTINGS_UPDATE: {
    id: 'system_settings_update',
    name: 'Update System Settings',
    description: 'Modify system configuration',
    requiredRole: 'Administrator' as UserRole,
  },

  // Event Testing
  EVENT_TEST_VIEW: {
    id: 'event_test_view',
    name: 'View Event Testing',
    description: 'View event testing tools and results',
    requiredRole: 'ReadOnly' as UserRole,
  },
  EVENT_TEST_EXECUTE: {
    id: 'event_test_execute',
    name: 'Execute Event Tests',
    description: 'Run event tests and simulations',
    requiredRole: 'PowerUser' as UserRole,
  },

  // History and Audit
} as const;

// Role hierarchy - higher roles inherit permissions from lower roles
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  'ReadOnly': 1,
  'PowerUser': 2,
  'Administrator': 3,
};

// Helper function to check if a role has sufficient permissions
export const hasPermission = (userRole: UserRole, requiredRole: UserRole): boolean => {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

// Helper function to get all permissions for a role
export const getPermissionsForRole = (role: UserRole): Permission[] => {
  return Object.values(PERMISSIONS).filter(permission => 
    hasPermission(role, permission.requiredRole)
  );
};

// Helper function to check if user can perform a specific action
export const canPerformAction = (userRole: UserRole, permissionId: keyof typeof PERMISSIONS): boolean => {
  const permission = PERMISSIONS[permissionId];
  return hasPermission(userRole, permission.requiredRole);
};
